import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { PropertyRegionStatisticsResponse } from "../response/PropertyRegionStatisticsResponse";

const API_VERSION = "v4";

export interface UsePropertyRegionStatisticsParams {
    start_date?: string | null;
    end_date?: string | null;
}

const usePropertyRegionStatistics = (params?: UsePropertyRegionStatisticsParams) => {
    return useQuery({
        queryKey: ['property-region-statistics', params?.start_date, params?.end_date],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/statistics/property-region`, {
                    params: {
                        start_date: params?.start_date ?? undefined,
                        end_date: params?.end_date ?? undefined,
                    }
                });

                const responseData = response.data;

                // Validate and normalize response
                const validationResult = PropertyRegionStatisticsResponse.safeParse(responseData);
                if (!validationResult.success) {
                    console.error("Validation failed for property-region statistics:", validationResult.error.errors);
                    
                    // Fallback: manually reduce/map the response in case schema validation fails
                    if (responseData && responseData.data) {
                        const rawData = responseData.data;
                        let normalized: Record<string, number> = {};
                        
                        if (Array.isArray(rawData)) {
                            rawData.forEach((item: any) => {
                                const name = item.region || item.region_name || item.name || '';
                                const total = item.total !== undefined ? item.total : (item.total_properties || 0);
                                if (name) {
                                    normalized[name] = total;
                                }
                            });
                        } else if (typeof rawData === 'object' && rawData !== null) {
                            normalized = rawData;
                        }
                        
                        return {
                            success: responseData.success ?? true,
                            message: responseData.message || "Manually parsed statistics by region",
                            code: responseData.code || 200,
                            data: normalized
                        };
                    }
                    throw new Error("Invalid property-region statistics data format");
                }
                
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch property-region statistics`, error);
                throw error;
            }
        },
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default usePropertyRegionStatistics;
